import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormWrapper } from 'uiComponents/form/form'
import FieldRow from './fieldRow'
import { InputCard } from 'uiComponents/cards'
import { CheckboxFieldFormik, SingleSelectFieldFormik, SliderFieldFormik } from 'uiComponents/input'
import GPSStudioLayout from './layout'
import { BaseRouteParams } from 'hocs'
import {
    useGetDefaultIndustrySettingsQuery,
    useGetGlobalAccountSettingsQuery,
    useSaveGlobalAccountSettingsMutation,
} from 'products/pricing/reduxQueries'
import { LoaderWrapper } from 'uiComponents/loaders'
import { Col, Row } from 'uiComponents/flex'
import { Body, Caption, Headline } from 'uiComponents/typography'
import NumberInputFieldFormik from 'uiComponents/input/numberInput/numberInputFieldFormik'
import { GlobalAccountSettings } from 'products/pricing/pricingService'
import ToggleFieldFormik from 'uiComponents/input/toggle/toggleFieldFormik'
import { useGetPricingModels } from './pricingModels'
import { pricerFormValidationSchema } from './formValidation'
import { useField, useFormikContext } from 'formik'
import PricingGoalSelectOption from './pricingGoalSelectOption'
import StudioProvider from './provider/provider'
import { convertTimeFormat } from 'utils'
import { useAppSelector } from 'store/hooks'
import { getIsUSPartner } from 'auth/selectors'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { t, useTranslation } from 'translations'

const getVenueTypeOptions = () =>
    [
        { value: 3, name: t('globalPricingSettings.studio.venueTypes.default') },
        { value: 1, name: t('globalPricingSettings.studio.venueTypes.indoor') },
        { value: 5, name: t('globalPricingSettings.studio.venueTypes.outdoor') },
    ] as any[]

const getPriceChangeLabels = () => ({
    1: t('globalPricingSettings.studio.priceChange.bigIncrease'),
    2: t('globalPricingSettings.studio.priceChange.increase'),
    3: t('globalPricingSettings.studio.priceChange.default'),
    4: t('globalPricingSettings.studio.priceChange.decrease'),
    5: t('globalPricingSettings.studio.priceChange.bigDecrease'),
})

interface VisitHoursLabelsProps {
    quarterName: string
    quarterTime: string
}

const VisitHoursLabels: React.FC<VisitHoursLabelsProps> = ({ quarterName, quarterTime }) => {
    return (
        <div style={{ width: '100%' }}>
            <Caption style={{ fontSize: '.625rem', lineHeight: '0.75rem', fontWeight: 500 }} color="light">
                {quarterName}
            </Caption>
            <Caption style={{ fontSize: '.625rem', lineHeight: '0.75rem' }} color="light">
                {quarterTime}
            </Caption>
        </div>
    )
}

const emptyObject = {}

const getWeekdays = () => [
    { index: 0, name: t('globalPricingSettings.studio.weekdays.mon') },
    { index: 1, name: t('globalPricingSettings.studio.weekdays.tue') },
    { index: 2, name: t('globalPricingSettings.studio.weekdays.wed') },
    { index: 3, name: t('globalPricingSettings.studio.weekdays.thu') },
    { index: 4, name: t('globalPricingSettings.studio.weekdays.fri') },
    { index: 5, name: t('globalPricingSettings.studio.weekdays.sat') },
    { index: 6, name: t('globalPricingSettings.studio.weekdays.sun') },
]

const getUsWeekdays = () => [
    { index: 6, name: t('globalPricingSettings.studio.weekdays.sun') },
    { index: 0, name: t('globalPricingSettings.studio.weekdays.mon') },
    { index: 1, name: t('globalPricingSettings.studio.weekdays.tue') },
    { index: 2, name: t('globalPricingSettings.studio.weekdays.wed') },
    { index: 3, name: t('globalPricingSettings.studio.weekdays.thu') },
    { index: 4, name: t('globalPricingSettings.studio.weekdays.fri') },
    { index: 5, name: t('globalPricingSettings.studio.weekdays.sat') },
]

const useUpdateIndustryDefaults = () => {
    const [{ value: pricingGoal }] = useField('pricingGoal')
    const { values, setFieldValue } = useFormikContext<GlobalAccountSettings>()
    const pricingGoalOptions = useGetPricingModels()
    const selectedPricingGoal = pricingGoal || pricingGoalOptions?.[0]?.value
    const [industryDefaults, setIndustryDefaults] = useState<GlobalAccountSettings>()

    const { data: industryDefaultsData } = useGetDefaultIndustrySettingsQuery({
        modelId: selectedPricingGoal,
        version: 'V2',
    })

    useEffect(() => {
        if (industryDefaultsData) {
            setIndustryDefaults(() => {
                const updatedDefaults: GlobalAccountSettings = {
                    ...industryDefaultsData,
                    pricingGoal: selectedPricingGoal,
                }

                Object.keys(updatedDefaults).forEach((key: keyof GlobalAccountSettings) => {
                    if (values[key] === undefined) {
                        setFieldValue(key, updatedDefaults[key])
                    }
                })

                return updatedDefaults
            })
        }
    }, [industryDefaultsData, selectedPricingGoal, setFieldValue, values])

    return industryDefaults
}

const GPSStudioFormContent = ({ accountSlug }: { accountSlug: string }) => {
    const { t } = useTranslation()
    const industryDefaults = useUpdateIndustryDefaults()
    const pricingGoalOptions = useGetPricingModels()
    const isUSPartner = useAppSelector(getIsUSPartner)
    const { values, setFieldValue, initialValues } = useFormikContext<GlobalAccountSettings>()

    const handleRewardEarlyBookingsToggle = (value: boolean) => {
        setFieldValue('earlyBookingsImpact', initialValues?.earlyBookingsImpact)
        setFieldValue('rewardEarlyBookings', value)
    }

    const handleChargeLateBookingsToggle = (value: boolean) => {
        setFieldValue('lateBookingsImpact', initialValues?.lateBookingsImpact)
        setFieldValue('chargeLateBookings', value)
    }

    return (
        <StudioProvider accountSlug={accountSlug}>
            <GPSStudioLayout>
                <InputCard style={{ marginBottom: '1.5rem' }}>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.goal.title')}
                        helperText={t('globalPricingSettings.studio.goal.helperText')}
                    >
                        <SingleSelectFieldFormik
                            id="pricingGoal"
                            name="pricingGoal"
                            SingleSelectOption={PricingGoalSelectOption}
                            options={pricingGoalOptions}
                        />
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.venueType.title')}
                        helperText={t('globalPricingSettings.studio.venueType.helperText')}
                    >
                        <SingleSelectFieldFormik
                            id="venueTypeOutdoor"
                            name="venueTypeOutdoor"
                            options={getVenueTypeOptions()}
                        />
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.weather.title')}
                        helperText={t('globalPricingSettings.studio.weather.helperText')}
                    >
                        <SliderFieldFormik
                            name="rainImpact"
                            id="rainImpact"
                            min={1}
                            max={5}
                            step={1}
                            minLabel={t('globalPricingSettings.studio.weather.minLabel')}
                            maxLabel={t('globalPricingSettings.studio.weather.maxLabel')}
                            recommendedValue={industryDefaults?.rainImpact}
                            recommendedValueDescription={t('globalPricingSettings.studio.recommendedValueDescription')}
                        />
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.temperature.title')}
                        helperText={t('globalPricingSettings.studio.temperature.helperText')}
                    >
                        <SliderFieldFormik
                            name="temperatureImpact"
                            id="temperatureImpact"
                            min={1}
                            max={5}
                            step={1}
                            minLabel={t('globalPricingSettings.studio.temperature.minLabel')}
                            maxLabel={t('globalPricingSettings.studio.temperature.maxLabel')}
                            recommendedValue={industryDefaults?.temperatureImpact}
                            recommendedValueDescription={t('globalPricingSettings.studio.recommendedValueDescription')}
                        />
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.nationalHolidays.title')}
                        helperText={t('globalPricingSettings.studio.nationalHolidays.helperText')}
                    >
                        <SliderFieldFormik
                            name="holidayImportance"
                            id="holidayImportance"
                            min={1}
                            max={5}
                            step={1}
                            minLabel={t('globalPricingSettings.studio.holidays.minLabel')}
                            maxLabel={t('globalPricingSettings.studio.holidays.maxLabel')}
                            recommendedValue={industryDefaults?.holidayImportance}
                            recommendedValueDescription={t('globalPricingSettings.studio.recommendedValueDescription')}
                        />
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.internationalHolidays.title')}
                        helperText={t('globalPricingSettings.studio.internationalHolidays.helperText')}
                    >
                        <SliderFieldFormik
                            name="audienceLocation"
                            id="audienceLocation"
                            min={1}
                            max={5}
                            step={1}
                            minLabel={t('globalPricingSettings.studio.holidays.minLabel')}
                            maxLabel={t('globalPricingSettings.studio.holidays.maxLabel')}
                            recommendedValue={industryDefaults?.audienceLocation}
                            recommendedValueDescription={t('globalPricingSettings.studio.recommendedValueDescription')}
                        />
                    </FieldRow>
                </InputCard>

                <InputCard style={{ marginBottom: '1.5rem' }}>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.gatePrice.title')}
                        helperText={t('globalPricingSettings.studio.gatePrice.helperText')}
                    >
                        <ToggleFieldFormik
                            id="boxOfficePriceOnVisitDate"
                            style={{ fontSize: '9px' }}
                            name="boxOfficePriceOnVisitDate"
                        />
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.earlyBookings.title')}
                        helperText={t('globalPricingSettings.studio.earlyBookings.helperText')}
                    >
                        <ToggleFieldFormik
                            id="rewardEarlyBookings"
                            style={{ fontSize: '9px', marginBottom: '0.65rem' }}
                            name="rewardEarlyBookings"
                            onClick={handleRewardEarlyBookingsToggle}
                        />
                        {values.rewardEarlyBookings && (
                            <SliderFieldFormik
                                name="earlyBookingsImpact"
                                min={1}
                                max={5}
                                step={1}
                                minLabel={t('globalPricingSettings.studio.earlyBookings.minLabel')}
                                maxLabel={t('globalPricingSettings.studio.earlyBookings.maxLabel')}
                                recommendedValue={industryDefaults?.earlyBookingsImpact}
                                recommendedValueDescription={t('globalPricingSettings.studio.recommendedValueDescription')}
                            />
                        )}
                    </FieldRow>
                    <FieldRow bottomSpacing="2rem" title={t('globalPricingSettings.studio.earlyBookings.minimumTitle')}>
                        <Row vAlign="center" gutter={8}>
                            <Col span={3}>
                                <NumberInputFieldFormik
                                    style={{ width: '100%' }}
                                    name="avgDaysTicketsBoughtInAdvance"
                                    min={1}
                                    maxLength={3}
                                    integerOnly
                                />
                            </Col>
                            <Col span="auto">
                                <Caption>{t('globalPricingSettings.studio.earlyBookings.daysInAdvance')}</Caption>
                            </Col>
                        </Row>
                    </FieldRow>
                    <FieldRow
                        bottomSpacing="2rem"
                        title={t('globalPricingSettings.studio.lateBookings.title')}
                        helperText={t('globalPricingSettings.studio.lateBookings.helperText')}
                    >
                        <ToggleFieldFormik
                            id="chargeLateBookings"
                            style={{ fontSize: '9px', marginBottom: '0.65rem' }}
                            name="chargeLateBookings"
                            onClick={handleChargeLateBookingsToggle}
                        />
                        {values.chargeLateBookings && (
                            <SliderFieldFormik
                                name="lateBookingsImpact"
                                min={1}
                                max={5}
                                step={1}
                                minLabel={t('globalPricingSettings.studio.lateBookings.minLabel')}
                                maxLabel={t('globalPricingSettings.studio.lateBookings.maxLabel')}
                                recommendedValue={industryDefaults?.lateBookingsImpact}
                                recommendedValueDescription={t('globalPricingSettings.studio.recommendedValueDescription')}
                            />
                        )}
                    </FieldRow>
                </InputCard>

                <InputCard style={{ marginBottom: '1.5rem', width: 'fit-content' }}>
                    <Headline style={{ margin: 0 }} size={6}>
                        {t('globalPricingSettings.studio.visitingHours.title')}
                    </Headline>
                    <Caption style={{ marginBottom: '1.5rem' }}>
                        {t('globalPricingSettings.studio.visitingHours.description')}
                    </Caption>
                    <Row style={{ marginBottom: '1.5rem' }}>
                        <Col
                            span="auto"
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                                marginBottom: '1rem',
                                paddingRight: '1.5rem',
                                maxWidth: 120,
                            }}
                        >
                            <div style={{ width: '100%', height: 35 }} />
                            <VisitHoursLabels
                                quarterName={t('globalPricingSettings.studio.visitingHours.morning')}
                                quarterTime={`(${convertTimeFormat('09:00')} - ${convertTimeFormat('12:00')})`}
                            />
                            <VisitHoursLabels
                                quarterName={t('globalPricingSettings.studio.visitingHours.afternoon')}
                                quarterTime={`(${convertTimeFormat('12:00')} - ${convertTimeFormat('16:00')})`}
                            />
                            <VisitHoursLabels
                                quarterName={t('globalPricingSettings.studio.visitingHours.evening')}
                                quarterTime={`(${convertTimeFormat('16:00')} - ${convertTimeFormat('20:00')})`}
                            />
                            <VisitHoursLabels
                                quarterName={t('globalPricingSettings.studio.visitingHours.night')}
                                quarterTime={`(${convertTimeFormat('20:00')} - ${convertTimeFormat('00:00')})`}
                            />
                        </Col>
                        {(isUSPartner ? getUsWeekdays() : getWeekdays()).map(({ index, name }) => {
                            return (
                                <Col
                                    key={name}
                                    span="auto"
                                    style={{ maxWidth: '60px', marginRight: '1rem', padding: 0 }}
                                >
                                    <InputCard style={{ padding: '1rem' }}>
                                        <Caption>{name}</Caption>
                                        <div style={{ marginBottom: '1rem' }} />
                                        <CheckboxFieldFormik
                                            defaultValue={undefined}
                                            value="morning"
                                            name={`daytimeImportance[${index}]`}
                                        />
                                        <div style={{ marginBottom: '1rem' }} />
                                        <CheckboxFieldFormik
                                            value="afternoon"
                                            name={`daytimeImportance[${index}]`}
                                        />
                                        <div style={{ marginBottom: '1rem' }} />
                                        <CheckboxFieldFormik
                                            value="evening"
                                            name={`daytimeImportance[${index}]`}
                                        />
                                        <div style={{ marginBottom: '1rem' }} />
                                        <CheckboxFieldFormik
                                            value="night"
                                            name={`daytimeImportance[${index}]`}
                                        />
                                        <div style={{ marginBottom: '1rem' }} />
                                    </InputCard>
                                </Col>
                            )
                        })}
                    </Row>
                </InputCard>
                <InputCard style={{ paddingBottom: '1.5rem', marginBottom: '1.5rem', width: 'fit-content' }}>
                    <Headline style={{ margin: 0 }} size={6}>
                        {t('globalPricingSettings.studio.weekdayAdjustment.title')}
                    </Headline>
                    <Caption style={{ marginBottom: '1.5rem' }}>
                        {t('globalPricingSettings.studio.weekdayAdjustment.description')}
                    </Caption>
                    <Row gutter={16}>
                        <Col
                            span="auto"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                                height: '136px',
                                margin: '1rem 1.5rem 0 0',
                            }}
                        >
                            {Object.entries(getPriceChangeLabels()).map(([key, val]) => (
                                <Body size={3} color="light" key={key}>
                                    {val}
                                </Body>
                            ))}
                        </Col>
                        {(isUSPartner ? getUsWeekdays() : getWeekdays()).map(({ name, index }) => (
                            <Col key={name} span="auto">
                                <InputCard
                                    style={{
                                        padding: '1rem .45rem',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        width: 52,
                                    }}
                                >
                                    <SliderFieldFormik
                                        style={{ height: 136 }}
                                        min={1}
                                        max={5}
                                        step={1}
                                        recommendedValue={industryDefaults?.weekdayImportance?.[index]}
                                        name={`weekdayImportance.[${index}]`}
                                        orientation="vertical"
                                    />
                                    <Caption
                                        style={{
                                            marginTop: '.5rem',
                                            textAlign: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        {name}
                                    </Caption>
                                </InputCard>
                            </Col>
                        ))}
                    </Row>
                </InputCard>
                <InputCard
                    style={{
                        marginBottom: '5.75rem',
                        paddingBottom: '1.5rem',
                        maxWidth: 'max-content',
                    }}
                >
                    <Headline style={{ margin: 0, width: 'fit-content' }} size={6}>
                        {t('globalPricingSettings.studio.monthAdjustment.title')}
                    </Headline>
                    <Caption style={{ marginBottom: '1.5rem', width: 'fit-content' }}>
                        {t('globalPricingSettings.studio.monthAdjustment.description')}
                    </Caption>
                    <Row gutter={8} style={{ width: 'fit-content' }}>
                        <Col
                            span="auto"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                                height: '136px',
                                margin: '1rem 1.5rem 0 0',
                            }}
                        >
                            {Object.entries(getPriceChangeLabels()).map(([key, val]) => (
                                <Body size={3} color="light" key={key}>
                                    {val}
                                </Body>
                            ))}
                        </Col>
                        {[
                            t('globalPricingSettings.studio.months.jan'),
                            t('globalPricingSettings.studio.months.feb'),
                            t('globalPricingSettings.studio.months.mar'),
                            t('globalPricingSettings.studio.months.apr'),
                            t('globalPricingSettings.studio.months.may'),
                            t('globalPricingSettings.studio.months.jun'),
                            t('globalPricingSettings.studio.months.jul'),
                            t('globalPricingSettings.studio.months.aug'),
                            t('globalPricingSettings.studio.months.sep'),
                            t('globalPricingSettings.studio.months.oct'),
                            t('globalPricingSettings.studio.months.nov'),
                            t('globalPricingSettings.studio.months.dec'),
                        ].map((month, index) => {
                            return (
                                <Col key={month} span="auto" style={{ maxWidth: '52px', marginRight: '.5rem' }}>
                                    <InputCard
                                        style={{
                                            padding: '1rem .45rem',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            width: 52,
                                        }}
                                    >
                                        <SliderFieldFormik
                                            style={{ height: 136 }}
                                            min={1}
                                            max={5}
                                            step={1}
                                            recommendedValue={industryDefaults?.monthImportance?.[index]}
                                            name={`monthImportance.[${index}]`}
                                            orientation="vertical"
                                        />
                                        <Caption
                                            style={{
                                                marginTop: '.5rem',
                                                textAlign: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            {month}
                                        </Caption>
                                    </InputCard>
                                </Col>
                            )
                        })}
                    </Row>
                </InputCard>
            </GPSStudioLayout>
        </StudioProvider>
    )
}

const GPSStudio = () => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const { data, isFetching } = useGetGlobalAccountSettingsQuery({ accountSlug })
    const [saveAccountSettings] = useSaveGlobalAccountSettingsMutation()

    const initialValues: Partial<GlobalAccountSettings> = useMemo(() => {
        return data?.settings ?? emptyObject
    }, [data])

    const submitValues = ({ features, ...values }: GlobalAccountSettings) => {
        saveAccountSettings({
            accountSlug,
            accountSettings: {
                ...values,
                avgDaysTicketsBoughtInAdvance: Number(values.avgDaysTicketsBoughtInAdvance),
            },
        })
            .unwrap()
            .then((response) => {
                if (response) {
                    showSuccessMessage(t('globalPricingSettings.studio.saveSuccess'))
                } else {
                    showErrorMessage(t('globalPricingSettings.studio.saveError'))
                }
            })
    }

    return (
        <LoaderWrapper loading={isFetching}>
            <FormWrapper
                enableReinitialize
                initialValues={initialValues}
                onSubmit={(values) => submitValues(values)}
                validationSchema={pricerFormValidationSchema}
                style={{ width: '100%' }}
            >
                {!isFetching && <GPSStudioFormContent accountSlug={accountSlug} />}
            </FormWrapper>
        </LoaderWrapper>
    )
}

export default GPSStudio
